import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';

import { Container, Row, Col, Button } from 'react-bootstrap';
import { FaPrint } from 'react-icons/fa';

import { parseISO, format } from 'date-fns';
import { useToast } from '../../../../hooks/toast';
import api from '../../../../services/api';

import getValidationErrors from '../../../../utils/getValidationErrors';
import { optionsTipoContratoMedico } from '../../../../utils/options';

import Input from '../../../../components/Input';
import InputFake from '../../../../components/InputFake';
import TextArea from '../../../../components/TextArea';
import SelectSync, { IOption } from '../../../../components/SelectSync';
import Loading from '../../../../components/Loading';

import { formatPrice, TIPO_USUARIO } from '../../../../utils/funcoes';
import Contrato from '../../../CartaoSaudeImpressao/contrato';
import getResponseErrors from '../../../../utils/getResponseErrors';
import { useAuth } from '../../../../hooks/auth';
import InputCheck from '../../../../components/InputCheck';
import InputMoney from '../../../../components/InputMoney';

type ComponentProps = {
  UsuCodigo: number;
};

interface IContratoBase {
  ConCodigo: number;
  ConUsuCodigo: number;
  ConDataInicio: Date;
  ConDataFim: Date;
  ConDataInicioFormatado: string;
  ConDataFimFormatado: string;
  ConTipo: string;
  ConRemuneracao: string;
  ConValor: number;
  ConValorFormatado: string;
  ConValorExtra: number;
  ConValorExtraFormatado: string;
  ConQuantidade: number;
  ConAtivo: boolean;
}

export interface IContrato extends IContratoBase {
  ConPagaRetorno: boolean;
}

interface IFormContrato extends IContratoBase {
  ConPagaRetorno: string;
}

export interface IUsuario {
  UsuCodigo: number;
  UsuNome: string;
  UsuCRM: string;
  UsuConselho: string;
}

interface IUsuarioContrato extends IUsuario {
  contrato?: IContrato[];
}

const ContratoUsuario: React.FC<ComponentProps> = ({ UsuCodigo }) => {
  const history = useHistory();
  const { user } = useAuth();

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const { addToast } = useToast();
  const formRefCadastro = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [usuario, setUsuario] = useState<IUsuario>();
  const [contratos, setContratos] = useState<IContrato[]>([]);
  const [exibeLista, setExibeLista] = useState(true);
  const [exibeValorExtra, setExibeValorExtra] = useState(true);

  const initialData = {
    ConDataInicio: format(new Date(), 'yyyy-MM-dd'),
    ConDataFim: format(new Date(), 'yyyy-MM-dd'),
    ConValorExtra: 0,
  };

  const handleFormataContrato = useCallback((dados: IContrato[]) => {
    const contratoFormatado = dados
      ? dados.map(con => {
          return {
            ...con,
            ConDataInicioFormatado: format(
              parseISO(con.ConDataInicio.toString()),
              'dd/MM/yyyy',
            ),
            ConDataFimFormatado: format(
              parseISO(con.ConDataFim.toString()),
              'dd/MM/yyyy',
            ),
            ConValorFormatado: formatPrice(con.ConValor),
            ConValorExtraFormatado: formatPrice(con.ConValorExtra),
          };
        })
      : [];
    return contratoFormatado;
  }, []);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IUsuarioContrato>(
          `/medicos/contratos/${UsuCodigo}`,
        );

        const { contrato, ...rest } = response.data;

        const contratoFormatado = handleFormataContrato(contrato || []);

        setUsuario(rest);
        setContratos(contratoFormatado);
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível carregar contrato',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    }
    if (UsuCodigo && UsuCodigo > 0) loadDados();
  }, [UsuCodigo, addToast, handleFormataContrato]);

  const handleSelecionaTipo = useCallback((dados: IOption) => {
    formRefCadastro.current?.setFieldValue('ConRemuneracao', dados.value);
    setExibeValorExtra(dados.value === 'Fechado com extra');
  }, []);

  const handleSubmitCadastro = useCallback(
    async (dados: IFormContrato) => {
      setLoading(true);
      try {
        formRefCadastro.current?.setErrors({});
        const schema = Yup.object().shape({
          ConDataInicio: Yup.date().required(),
          ConDataFim: Yup.date().required(),
          ConTipo: Yup.string().required(),
          ConRemuneracao: Yup.string().required(),
          ConValor: Yup.number().required(),
          ConValorExtra: Yup.number().required(),
          ConQuantidade: Yup.number().required(),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const ConTipo =
          optionsTipoContratoMedico.find(opt => opt.value === dados.ConTipo)
            ?.label || '';

        const response = await api.post<IContrato>(`/medicos/contratos`, {
          ...dados,
          ConDataInicio: new Date(`${dados.ConDataInicio} 03:00:00`),
          ConDataFim: new Date(`${dados.ConDataFim} 03:00:00`),
          ConUsuCodigo: UsuCodigo,
          ConTipo,
          ConAtivo: true,
          ConPagaRetorno: dados.ConPagaRetorno === 'true',
        });

        const contratoFormatado = handleFormataContrato([response.data]);

        setContratos(state => {
          return state.concat(contratoFormatado);
        });
        setExibeLista(true);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefCadastro.current?.setErrors(errors);
          return;
        }

        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível salvar usuário',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, UsuCodigo, handleFormataContrato],
  );

  return (
    <Container style={{ height: '100%', minHeight: '400px' }}>
      {loading && <Loading />}

      {usuario && (
        <>
          <Row>
            <Col md={7}>
              <InputFake label="Médico" value={usuario.UsuNome} />
            </Col>
            <Col md={2}>
              <InputFake
                label="Registro"
                value={`${usuario.UsuConselho} ${usuario.UsuCRM}`}
              />
            </Col>
            <Col md={3}>
              <br />
              <Button
                type="button"
                variant="outline-primary mt-1"
                size="sm"
                onClick={() => {
                  setExibeLista(false);
                }}
              >
                Novo Contrato
              </Button>
            </Col>
          </Row>
          {exibeLista && Contrato.length > 0 ? (
            <table
              className="table table-hover table-sm table-font-small mt-3"
              style={{ borderWidth: '0px' }}
            >
              <thead>
                <tr className="bg-light" style={{ fontSize: 'Small' }}>
                  <th scope="col">Início</th>
                  <th scope="col">Fim</th>
                  <th scope="col">Tipo</th>
                  <th scope="col">Valor</th>
                  <th scope="col">Nº Atendimentos</th>
                  <th scope="col">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {contratos.map(con => (
                  <tr
                    key={`con-${con.ConCodigo}`}
                    style={{ fontSize: 'Small', height: '30px' }}
                  >
                    <td>{con.ConDataInicioFormatado}</td>
                    <td>{con.ConDataFimFormatado}</td>
                    <td>{con.ConTipo}</td>
                    <td>{con.ConValorFormatado}</td>
                    <td>{con.ConQuantidade}</td>
                    <td>
                      <Link
                        className="btn btn-sm btn-outline-primary py-0"
                        to={`/contratomedico/${con.ConCodigo}`}
                        target="_blank"
                      >
                        <FaPrint />
                      </Link>
                      {/* <Button
                        type="button"
                        variant="outline-primary"
                        size="sm"
                        className="py-0"
                        onClick={() => {
                          console.log('');
                        }}
                      >
                        <FaPrint />
                      </Button> */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Form
              ref={formRefCadastro}
              className="mt-2"
              initialData={initialData}
              onSubmit={handleSubmitCadastro}
            >
              <h5 className="text-muted border-bottom">Dados do Contrato</h5>
              <Row>
                <Col md={4}>
                  <Input
                    label="Início Contrato"
                    name="ConDataInicio"
                    type="date"
                  />
                </Col>
                <Col md={4}>
                  <Input label="Fim Contrato" name="ConDataFim" type="date" />
                </Col>
                <Col md={4}>
                  <SelectSync
                    label="Tipo Remuneração"
                    name="ConTipo"
                    options={optionsTipoContratoMedico}
                    handleSelectChange={handleSelecionaTipo}
                  />
                </Col>
                <div className="w-100 mt-md-2" />
                <Col md={3}>
                  <Input label="Valor" name="ConValor" />
                </Col>
                <Col md={3}>
                  <Input
                    label="Nº Atendimentos"
                    name="ConQuantidade"
                    type="number"
                  />
                </Col>
                <Col md={3} className={`${exibeValorExtra} ? '': 'd-none`}>
                  <InputMoney
                    label="Valor Extra"
                    name="ConValorExtra"
                    autoComplete="nope"
                    placeholder="Valor Extra"
                  />
                </Col>

                <Col md={3}>
                  <InputCheck
                    name="ConPagaRetorno"
                    label="Paga Retorno"
                    isDisabled={false}
                    classSpam={{
                      display: 'block',
                      paddingRight: '10px',
                      marginBottom: '9px',
                      fontWeight: 'bold',
                    }}
                  />
                </Col>

                <div className="w-100 mt-md-2" />
                <Col md={12}>
                  <TextArea
                    label="Remuneração"
                    name="ConRemuneracao"
                    rows={4}
                  />
                </Col>
                <div className="w-100 mt-md-3" />
                <Col md={3}>
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    disabled={loading}
                  >
                    Salvar Contrato
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </>
      )}
    </Container>
  );
};

export default ContratoUsuario;
